import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const MobileContext = createContext();

export const MOBILE_BREAKPOINT = 768;

export const MobileProvider = ({ children }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
    const [isPwa, setIsPwa] = useState(false);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

    const handleResize = useCallback(() => {
        const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
        setIsMobile(mobile);
    }, []);

    // Check if app is running as PWA
    useEffect(() => {
        const checkPwaStatus = () => {
            // iOS detection
            const isIOSPwa = window.navigator.standalone;

            // Android/Chrome detection
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

            setIsPwa(isIOSPwa || isStandalone);
        };

        checkPwaStatus();

        // Also check when visibility changes (app might switch between modes)
        document.addEventListener('visibilitychange', checkPwaStatus);
        return () => document.removeEventListener('visibilitychange', checkPwaStatus);
    }, []);

    // Detect keyboard visibility
    useEffect(() => {
        if (!isMobile) return;

        let initialHeight = window.innerHeight;

        const detectKeyboard = () => {
            // Using visual viewport API if available (more accurate)
            if (window.visualViewport) {
                const viewportHeight = window.visualViewport.height;
                const heightDifference = initialHeight - viewportHeight;

                // If height difference is significant, keyboard is likely open
                // The threshold value might need adjustment based on testing
                setIsKeyboardVisible(heightDifference > 150);
            } else {
                // Fallback method
                const heightDifference = initialHeight - window.innerHeight;
                setIsKeyboardVisible(heightDifference > 150);
            }
        };

        const orientationChangeHandler = () => {
            // Reset initial height after orientation change
            setTimeout(() => {
                initialHeight = window.innerHeight;
            }, 300);
        };

        window.addEventListener('resize', detectKeyboard);
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', detectKeyboard);
        }
        window.addEventListener('orientationchange', orientationChangeHandler);

        return () => {
            window.removeEventListener('resize', detectKeyboard);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', detectKeyboard);
            }
            window.removeEventListener('orientationchange', orientationChangeHandler);
        };
    }, [isMobile]);

    // Handle window resize to detect mobile/desktop
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    return (
        <MobileContext.Provider value={{ isMobile, isPwa, isKeyboardVisible }}>
            {children}
        </MobileContext.Provider>
    );
};

export const useMobile = () => {
    const context = useContext(MobileContext);
    if (context === undefined) {
        throw new Error('useMobile must be used within a MobileProvider');
    }
    return context;
};
